import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { BackendService } from './general/backend.service';

export type AttachmentKind = 'pdf' | 'image' | 'docx' | 'file' | 'link' | 'pubmed';

export interface StatementAttachment {
  id: number;
  type: 'FILE' | 'LINK' | 'PUBMED';
  title: string;
  link?: string | null;
  ext?: string | null;
  size?: number | null;
  reference?: string | null;
  page?: number | null;
  position?: number | null;
  is_primary?: boolean;
  is_read_only?: boolean;
  created_at?: string;
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const PAGE_PATTERN = /\b(?:pag(?:ina)?|pp?|page)\.?\s*(\d{1,4})/i;

@Injectable({
  providedIn: 'root',
})
export class AttachmentPreviewService implements OnDestroy {
  private blobs = new Map<string, Promise<Blob>>();
  private pdfs = new Map<string, Promise<PDFDocumentProxy>>();
  private objectUrls = new Map<string, string>();
  private pdfjs: Promise<typeof import('pdfjs-dist')> | null = null;

  constructor(private backend: BackendService) {}

  ngOnDestroy(): void {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.pdfs.forEach((pdf) => pdf.then((doc) => doc.destroy()).catch(() => undefined));
  }

  kind(attachment: StatementAttachment): AttachmentKind {
    if (attachment.type === 'PUBMED') {
      return 'pubmed';
    }
    if (attachment.type === 'LINK') {
      return 'link';
    }
    const ext = (attachment.ext || attachment.title?.split('.').pop() || '').toLowerCase();
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (IMAGE_EXTENSIONS.includes(ext)) {
      return 'image';
    }
    if (ext === 'docx') {
      return 'docx';
    }
    return 'file';
  }

  icon(attachment: StatementAttachment): string {
    switch (this.kind(attachment)) {
      case 'pubmed':
        return 'science';
      case 'link':
        return 'link';
      case 'docx':
        return 'description';
      case 'image':
        return 'image';
      case 'pdf':
        return 'picture_as_pdf';
      default: {
        const ext = (attachment.ext || '').toLowerCase();
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
          return 'table_chart';
        }
        if (['ppt', 'pptx', 'key'].includes(ext)) {
          return 'slideshow';
        }
        if (['zip', 'rar', '7z'].includes(ext)) {
          return 'folder_zip';
        }
        return 'insert_drive_file';
      }
    }
  }

  isViewableInApp(attachment: StatementAttachment): boolean {
    return ['pdf', 'image', 'docx'].includes(this.kind(attachment));
  }

  pageOf(attachment: StatementAttachment): number {
    if (attachment.page && attachment.page > 0) {
      return attachment.page;
    }
    const match = (attachment.reference || '').match(PAGE_PATTERN);
    return match ? Math.max(1, parseInt(match[1], 10)) : 1;
  }

  blob(surveyId: number, questionId: number, attachment: StatementAttachment): Promise<Blob> {
    const key = this.key(surveyId, questionId, attachment);
    if (!this.blobs.has(key)) {
      const request = firstValueFrom(
        this.backend.getBlob(`surveys/${surveyId}/questions/${questionId}/attachments/${attachment.id}/content`)
      ).then((response: any) => (response?.body ?? response) as Blob);
      request.catch(() => this.blobs.delete(key));
      this.blobs.set(key, request);
    }
    return this.blobs.get(key)!;
  }

  async objectUrl(surveyId: number, questionId: number, attachment: StatementAttachment): Promise<string> {
    const key = this.key(surveyId, questionId, attachment);
    if (!this.objectUrls.has(key)) {
      const blob = await this.blob(surveyId, questionId, attachment);
      this.objectUrls.set(key, URL.createObjectURL(blob));
    }
    return this.objectUrls.get(key)!;
  }

  pdf(surveyId: number, questionId: number, attachment: StatementAttachment): Promise<PDFDocumentProxy> {
    const key = this.key(surveyId, questionId, attachment);
    if (!this.pdfs.has(key)) {
      const loading = Promise.all([this.loadPdfjs(), this.blob(surveyId, questionId, attachment)]).then(
        async ([pdfjs, blob]) => pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise
      );
      loading.catch(() => this.pdfs.delete(key));
      this.pdfs.set(key, loading);
    }
    return this.pdfs.get(key)!;
  }

  async renderPdfPage(pdf: PDFDocumentProxy, pageNumber: number, canvas: HTMLCanvasElement, scale: number): Promise<{ width: number; height: number }> {
    const page = await pdf.getPage(Math.min(Math.max(1, pageNumber), pdf.numPages));
    const ratio = window.devicePixelRatio || 1;
    const viewport = page.getViewport({ scale: scale * ratio });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
    canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
    const context = canvas.getContext('2d');
    if (!context) {
      return { width: 0, height: 0 };
    }
    await page.render({ canvasContext: context, viewport }).promise;
    return { width: viewport.width / ratio, height: viewport.height / ratio };
  }

  async renderPdfThumbnail(pdf: PDFDocumentProxy, pageNumber: number, canvas: HTMLCanvasElement, width: number): Promise<void> {
    const page = await pdf.getPage(Math.min(Math.max(1, pageNumber), pdf.numPages));
    const base = page.getViewport({ scale: 1 });
    await this.renderPdfPage(pdf, pageNumber, canvas, width / base.width);
  }

  async drawImage(url: string, canvas: HTMLCanvasElement, scale: number): Promise<{ width: number; height: number }> {
    const image = await this.loadImage(url);
    const ratio = window.devicePixelRatio || 1;
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${Math.floor(width)}px`;
    canvas.style.height = `${Math.floor(height)}px`;
    const context = canvas.getContext('2d');
    context?.setTransform(ratio, 0, 0, ratio, 0, 0);
    context?.drawImage(image, 0, 0, width, height);
    return { width: image.naturalWidth, height: image.naturalHeight };
  }

  loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
  }

  private loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
    if (!this.pdfjs) {
      this.pdfjs = import('pdfjs-dist').then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';
        return pdfjs;
      });
    }
    return this.pdfjs;
  }

  private key(surveyId: number, questionId: number, attachment: StatementAttachment): string {
    return `${surveyId}:${questionId}:${attachment.type}:${attachment.id}`;
  }
}
